import type { PageMessage } from '../../../../types/PageMessage';
import { Button } from '../../../cdl/Button/Button';
import { PaginatedAsyncComboBox } from '../../../cdl/ComboBox/ComboBox';
import type { PaginatedAsyncComboBoxProps } from '../../../cdl/ComboBox/ComboBox';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import type { ProductComboBoxOptionsParams } from '../../api/queryKeys/products';
import { queries } from '../../api/queryKeys/queries';
import { translate } from '../../helpers/translate.helper';
import { IconArrowRight } from '../../icons/cdl/ArrowRight';
import { Box } from '../../ui/Box';

import { CustomProductOption } from './components/CustomProductOption';
import { ProductSingleValue } from './components/ProductSingleValue';
import type { ProductOption } from './ProductOption';

interface ProductSelectControlProps
    extends Omit<
        PaginatedAsyncComboBoxProps<ProductOption>,
        'onChange' | 'value' | 'optionsQueryKey' | 'optionsQueryFn' | 'selectedOptionQueryKey' | 'selectedOptionQueryFn'
    > {
    hideSuccessorProduct?: boolean;
    loadOptions: (searchQuery: string, page: number) => Promise<PageMessage<ProductOption>>;
    loadSelectedOption: (productId: string) => Promise<ProductOption | null>;
    optionsQueryParams: Omit<ProductComboBoxOptionsParams, 'searchQuery'>;
    value: string;
    selectedValue: ProductOption | null;
    onSelectedValueChange: (option: ProductOption | null) => void;
}

export const ProductSelectControl = ({
    hideSuccessorProduct = false,
    loadOptions,
    loadSelectedOption,
    optionsQueryParams,
    value,
    selectedValue,
    onSelectedValueChange,
    ...rest
}: ProductSelectControlProps) => {
    return (
        <div>
            <PaginatedAsyncComboBox<ProductOption>
                {...rest}
                value={value || null}
                optionsQueryKey={(search) =>
                    queries.products.comboBoxOptions({ ...optionsQueryParams, searchQuery: search }).queryKey
                }
                optionsQueryFn={({ search, page }) => loadOptions(search, page)}
                selectedOptionQueryKey={(id) =>
                    queries.products.selectedComboBoxOption({
                        defaultProductIds: optionsQueryParams.defaultProductIds,
                        productId: id,
                    }).queryKey
                }
                selectedOptionQueryFn={loadSelectedOption}
                OptionComponent={CustomProductOption}
                ValueComponent={(props) => <ProductSingleValue {...props} disabled={rest.disabled} />}
                onChange={(_, meta) => onSelectedValueChange(meta.option)}
            />

            {selectedValue?.product.latestSuccessorProduct && !rest.disabled && !hideSuccessorProduct ? (
                <Box marginTop={8}>
                    <Tooltip side="right" label={translate('order.productSuccessorHintTooltip')}>
                        <Button
                            emphasis="low"
                            type="button"
                            onClick={() =>
                                onSelectedValueChange({
                                    value: selectedValue.product.latestSuccessorProductId!,
                                    label: selectedValue.product.latestSuccessorProduct?.name!,
                                    product: selectedValue.product.latestSuccessorProduct!,
                                })
                            }
                            LeadingIcon={IconArrowRight}
                        >
                            {selectedValue.product.latestSuccessorProduct.name}
                        </Button>
                    </Tooltip>
                </Box>
            ) : null}
        </div>
    );
};
